import type { Asset } from '../types/common.types'

/**
 * A video whose file could not be ingested is returned with a thumbnail only.
 * See https://docs.metapic.dev/engineering/image-processing
 */
export const isThumbnailOnlyVideo = (asset: Pick<Asset, 'type' | 'url'>) =>
  asset.type === 'video' && !asset.url

/**
 * Videos that ship a thumbnail render it as the poster and only download the
 * video file once the user hits play. Without a thumbnail we keep loading the
 * metadata up front, since that is the only way to show a first frame.
 */
export const resolveVideoPreload = (asset: Pick<Asset, 'thumbnailUrl'>): 'none' | 'metadata' =>
  asset.thumbnailUrl ? 'none' : 'metadata'

/**
 * Resolves the media url that can be downloaded directly for an asset. Falls
 * back to the thumbnail when the video file is missing, since that is the only
 * media we have. Assets that carry an id are downloaded through the
 * download-url endpoint instead, which names the file itself.
 */
export const resolveDownloadableAssetUrl = (
  asset: Pick<Asset, 'type' | 'url' | 'thumbnailUrl'>,
): string | null => {
  const url = isThumbnailOnlyVideo(asset) ? asset.thumbnailUrl : asset.url

  return url || null
}
